import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DialogTitleWithClose } from "./DialogTitleWithClose";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { makeDateString } from "../api/types";
import type {
  AutoTxId,
  CategoryId,
  ExpenseTypeId,
  GroupId,
  InvoiceId,
  SourceId,
} from "../api/types";
import { DatePicker } from "./DatePicker";
import { useRegistry } from "../context/RegistryContext";
import {
  AutoTransactionPickerField,
  CategoryPickerField,
  ExpenseTypePickerField,
  GroupPickerField,
  SourcePickerField,
} from "./EntityPickerField";
import type { FiltersWithPeriod } from "../logic/filter-logic";

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  filters: FiltersWithPeriod;
  onApply: (filters: FiltersWithPeriod) => void;
  invoices?: InvoiceId[];
}

export const FilterModal = ({
  open,
  onClose,
  filters,
  onApply,
  invoices = [],
}: FilterModalProps) => {
  const [localFilters, setLocalFilters] = useState<FiltersWithPeriod>(filters);
  const reg = useRegistry();

  // Backend filter is a vec but the UI is single-select for now — the user
  // typically wants "show me what this one rule hits". The first id in the
  // array drives the picker; clearing wipes the array.
  const selectedAutoTxId: AutoTxId | null =
    localFilters.autoTransactionId?.[0] ?? null;

  const sourceIds = (localFilters.source ?? [])
    .map((name) => reg.sources.find((s) => s.name === name)?.id)
    .filter((id): id is SourceId => id !== undefined);
  const expenseTypeIds = (localFilters.expenseType ?? [])
    .map((name) => reg.expenseTypes.find((e) => e.name === name)?.id)
    .filter((id): id is ExpenseTypeId => id !== undefined);
  const groupIds = (localFilters.group ?? [])
    .map((name) => reg.groups.find((g) => g.name === name)?.id)
    .filter((id): id is GroupId => id !== undefined);
  const categoryIds = (localFilters.category ?? [])
    .map((name) => reg.categories.find((c) => c.name === name)?.id)
    .filter((id): id is CategoryId => id !== undefined);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters((prev) => ({
      ...prev,
      period: null,
      startDate: null,
      endDate: null,
      source: [],
      expenseType: [],
      group: [],
      category: [],
      merchant: "",
      description: "",
      autoTransactionId: [],
      ignored: null,
      matchProblem: null,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitleWithClose onClose={onClose}>
        Filter Transactions
      </DialogTitleWithClose>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <DatePicker
              mode="range"
              label="Date range"
              fullWidth
              value={{
                from: localFilters.startDate ?? null,
                to: localFilters.endDate ?? null,
                period: localFilters.period ?? null,
              }}
              onChange={({ from, to, period }) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  period: period ?? null,
                  startDate: period || !from ? null : makeDateString(from),
                  endDate: period || !to ? null : makeDateString(to),
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Merchant (contains)"
              fullWidth
              size="small"
              value={localFilters.merchant || ""}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  merchant: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Description (contains)"
              fullWidth
              size="small"
              value={localFilters.description || ""}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <SourcePickerField
              multiple
              label="Source"
              size="small"
              value={sourceIds}
              onChange={(ids) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  source: ids
                    .map((id) => reg.sources.find((s) => s.id === id)?.name)
                    .filter((n): n is string => typeof n === "string"),
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <MultiSelectFilter
              label="Invoice"
              options={invoices}
              selected={localFilters.invoice || []}
              onChange={(val) =>
                setLocalFilters((prev) => ({ ...prev, invoice: val }))
              }
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ExpenseTypePickerField
              multiple
              label="Expense Type"
              size="small"
              value={expenseTypeIds}
              onChange={(ids) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  expenseType: ids
                    .map(
                      (id) => reg.expenseTypes.find((e) => e.id === id)?.name,
                    )
                    .filter((n): n is string => typeof n === "string"),
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <GroupPickerField
              multiple
              label="Group"
              size="small"
              value={groupIds}
              onChange={(ids) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  group: ids
                    .map((id) => reg.groups.find((g) => g.id === id)?.name)
                    .filter((n): n is string => typeof n === "string"),
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CategoryPickerField
              multiple
              label="Category"
              size="small"
              value={categoryIds}
              onChange={(ids) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  category: ids
                    .map((id) => reg.categories.find((c) => c.id === id)?.name)
                    .filter((n): n is string => typeof n === "string"),
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <AutoTransactionPickerField
              size="small"
              value={selectedAutoTxId}
              onChange={(id) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  autoTransactionId: id ? [id] : [],
                }))
              }
              allowEmpty
              emptyLabel="Any rule"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFilters.ignored === true}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      ignored: e.target.checked ? true : null,
                    }))
                  }
                />
              }
              label="Show ignored transactions"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFilters.matchProblem === true}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      matchProblem: e.target.checked ? true : null,
                    }))
                  }
                />
              }
              // Reachable from the Home warning's link, so it has to be
              // visible — and clearable — once you land here.
              label="Only where a rule couldn't dispatch"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear} color="secondary">
          Clear All
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};
